import { useState, useEffect, useRef, useCallback } from 'react';
import $ from 'jquery';
import _ from 'underscore';
import classNames from 'classnames';
import LayoutSource from 'apps/layout/sources/layout-source';
import WindowOverflowMixin from 'mixins/window-overflow-mixin';
import PropTypes from 'prop-types';

const EllipsisOverflow = ({ children, component: Component = 'div', showTooltip = true, className }) => {
  const [overlayKey, setOverlayKey] = useState(null);
  const concealedRef = useRef(null);
  const isMounted = useRef(true);
  const revealDelayRef = useRef(null);

  useEffect(() => {
    return () => {
      isMounted.current = false;
      clearTimeout(revealDelayRef.current);
      if (overlayKey) {
        LayoutSource.removeOverlay(overlayKey);
      }
    };
  }, [overlayKey]);

  const getFullWidth = () => {
    const element = concealedRef.current;
    if (!element) return 0;

    const ruler = $(`<span class='overflow-ruler'>${$(element).html()}</span>`);
    $('body').append(ruler);
    ruler.css({
      'font-family': $(element).css('font-family'),
      'font-size': $(element).css('font-size'),
      visibility: 'hidden',
    });

    const fullWidth = ruler.width();
    ruler.remove();
    return fullWidth;
  };

  const getHeight = () => {
    const element = concealedRef.current;
    return $(element).height();
  };

  const renderOverlay = useCallback(() => {
    const getWidthDiff = () => {
      const element = concealedRef.current;
      if (element && $(element).width() > 0) {
        return getFullWidth() - $(element).width();
      }
      return 0;
    };

    const getOverlay = () => {
      return (
        <div
          className={classNames('ellipsis-overflow--content', {
            'is-max-width': getFullWidth() > 800,
          })}
        >
          {children}
        </div>
      );
    };

    if (!isMounted.current) return;

    const domProps = WindowOverflowMixin.getDOMProperties(concealedRef.current);

    const key = overlayKey || _.randomKey();

    if (getWidthDiff() > 0) {
      setOverlayKey(key);

      let top = domProps.top + getHeight();

      // Adjust position left if it extends beyond window
      const windowWidth = $(window).outerWidth();
      const fullWidth = getFullWidth() + 14; //14 is for padding and border on ellipsis-overflow--content

      if (fullWidth + domProps.left > windowWidth) {
        domProps.left = windowWidth - fullWidth;
      }

      LayoutSource.overlay(getOverlay(), {
        ...domProps,
        key,
        top,
      });
    }
  }, [overlayKey, children]);

  const handleReveal = () => {
    if (!showTooltip) return;
    clearTimeout(revealDelayRef.current);
    revealDelayRef.current = setTimeout(() => {
      renderOverlay();
    }, 400);
  };

  const handleClose = () => {
    clearTimeout(revealDelayRef.current);
    if (overlayKey) {
      LayoutSource.removeOverlay(overlayKey);
    }
  };

  return (
    <div className="overflow-text" onMouseOver={handleReveal} onMouseOut={handleClose}>
      <Component className={classNames('ellipsis-overflow', className)} ref={concealedRef}>
        {children}
      </Component>
    </div>
  );
};

export default EllipsisOverflow;

EllipsisOverflow.propTypes = {
  children: PropTypes.node,
  component: PropTypes.elementType,
  showTooltip: PropTypes.bool,
  className: PropTypes.string,
};
